import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getCurrentSubscription, getInvoiceUsage } from "../Services/SubscriptionService";
import { ISubscriptionPlan, IOrganizationSubscription, IInvoiceUsage } from "../Interfaces/ISubscription";

// NOTE: I didn't find an existing global state mechanism (Redux/Zustand/etc.)
// in the supplied code — LoginPage.tsx only uses localStorage + a Session
// util. React Context is therefore the lightest addition consistent with
// requirement #21. If your project does have a global store elsewhere,
// wire refreshSubscription()/the state below into that instead of this
// Context and drop SubscriptionProvider.

interface ISubscriptionContextValue {
  subscription: IOrganizationSubscription | null;
  plan: ISubscriptionPlan | null;
  isActive: boolean;
  isExpired: boolean;
  loading: boolean;
  invoiceUsage: IInvoiceUsage | null;
  hasFeature: (feature: string) => boolean;
  /** UX hint only — the backend (requireInvoiceCreateAccess) is authoritative. */
  hasAction: (feature: string, action: string) => boolean;
  canCreateInvoice: boolean;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<ISubscriptionContextValue | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<IOrganizationSubscription | null>(null);
  const [plan, setPlan] = useState<ISubscriptionPlan | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [invoiceUsage, setInvoiceUsage] = useState<IInvoiceUsage | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = useCallback(async () => {
    // ── NEW: never call the API without a token. SubscriptionProvider
    // sits above <Router> so it's mounted on the public /login page too,
    // before any auth exists. Without this guard, an unauthenticated
    // request 401s, your axios interceptor redirects back to "/", the
    // provider remounts, and it fires again — a tight reload loop.
    const hasToken = !!localStorage.getItem("accessToken");
    if (!hasToken) {
      setSubscription(null);
      setPlan(null);
      setIsActive(false);
      setIsExpired(false);
      setInvoiceUsage(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const current = await getCurrentSubscription();
      setSubscription(current.subscription);
      setPlan(current.plan);
      setIsActive(current.isActive);
      setIsExpired(current.isExpired);

      // Only fetch usage when invoicing is actually enabled — avoids an
      // extra request that's guaranteed to fail for plans without it.
      if (current.plan?.features?.invoice?.enabled) {
        try {
          setInvoiceUsage(await getInvoiceUsage());
        } catch {
          setInvoiceUsage(null);
        }
      } else {
        setInvoiceUsage(null);
      }
    } catch (error) {
      console.error("Failed to load subscription:", error);
      setSubscription(null);
      setPlan(null);
      setIsActive(false);
      setIsExpired(false);
      setInvoiceUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const hasFeature = useCallback(
    (feature: string) => {
      if (!plan) return false;
      const featureConfig = (plan.features as any)?.[feature];
      return !!featureConfig?.enabled;
    },
    [plan]
  );
 // ── SUB-MENU-level: one item inside an enabled feature. Mirrors the
  // backend's canPerformAction exactly:
  //   - feature must be enabled at all (hasFeature check first)
  //   - if plan.features.<feature>.actions doesn't exist, or doesn't define
  //     this action key, it's allowed by default
  //   - if it's explicitly false, it's locked
  // Keeping this identical to canPerformAction means the sidebar lock and
  // the backend's real enforcement can never disagree with each other. ────
  const hasAction = useCallback(
    (feature: string, action: string) => {
      if (!hasFeature(feature)) return false;
      const featureConfig = (plan?.features as any)?.[feature];
      const actions = featureConfig?.actions;
      if (!actions || !Object.prototype.hasOwnProperty.call(actions, action)) return true;
      return actions[action] === true;
    },
    [plan, hasFeature]
  );

  const canCreateInvoice = useMemo(() => {
    if (!isActive || !hasFeature("invoice")) return false;
    if (!invoiceUsage) return true; // unknown/loading — backend has the final word
    if (invoiceUsage.unlimited) return true;
    return (invoiceUsage.remaining ?? 0) > 0;
  }, [isActive, hasFeature, invoiceUsage]);

  const value: ISubscriptionContextValue = {
    subscription,
    plan,
    isActive,
    isExpired,
    loading,
    invoiceUsage,
    hasFeature,
    hasAction,
    canCreateInvoice,
    refreshSubscription,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = (): ISubscriptionContextValue => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within a <SubscriptionProvider>");
  }
  return ctx;
};